import { IColumnHeaderFilterProps } from 'devextreme-react/data-grid';
import { ColumnProps } from './DataGridProps';
import { get, has } from 'lodash';
import { ApolloClient } from '@apollo/client';
import { isType } from '#/common/utils/typeGuards';
import { createDataSource } from '#/common/utils/createDataSource';

export const getColumnHeaderFilter = <T>(
  gqlClient: ApolloClient<object>,
  column: ColumnProps<T>,
): IColumnHeaderFilterProps => {
  const { headerFilter, dataField, lookup } = column;
  if (!headerFilter) {
    if (!lookup) {
      return {};
    }
    const { dataSource, displayExpr = 'text', valueExpr = 'value' } = lookup;
    return {
      dataSource: isType(dataSource, 'array')
        ? dataSource.map((item) => ({
            text: get(item, displayExpr, ''),
            value: get(item, valueExpr, ''),
          }))
        : dataSource,
    };
  }

  if (!('store' in headerFilter) && !('getItemList' in headerFilter)) {
    return headerFilter;
  }

  const map = (item: any) => {
    const displayValue = get(item, headerFilter.displayExpr, '');
    const valueExpr = headerFilter.valueExpr ?? headerFilter.displayExpr;
    const value = get(item, valueExpr, '');

    return {
      ...item,
      text: displayValue,
      value: headerFilter.filterType === 'contains' ? [dataField, 'contains', value] : value,
    };
  };

  if ('store' in headerFilter) {
    return {
      dataSource: {
        store: headerFilter.store,
        map,
      },
    };
  }

  if ('getItemList' in headerFilter) {
    return {
      dataSource: {
        store: createDataSource<any>(gqlClient, {
          ...headerFilter,
          cacheEnabled: true,
          searchKey: headerFilter.displayExpr,
        }),
        map,
      },
    };
  }

  return headerFilter;
};

export const getElementPosition = (el?: HTMLDivElement | null): DOMRect => {
  if (el) {
    const rect = el.getBoundingClientRect();
    return rect;
  }

  return { bottom: 0, left: 0, top: 0, right: 0, x: 0, y: 0 } as DOMRect;
};

/**
 * WARNING: Use this function only with DATE values, not with DATETIME!
 *
 * DevExtreme tries to convert both date and datetime values to UTC.
 * This conversion works for datetime values, but we store date values in UTC and use them only in UTC.
 * We cannot stop this auto-conversion, but where needed we can edit the date, to overcome this limitation.
 *
 * This function edits the date: it removes the timezone offset from its value.
 * So, when DX converts to UTC we will still get the correct date value.
 *
 * If the date value already has time 00:00:00.000 in UTC: we do not apply any conversion.
 * In this case, the fix is already applied.
 *
 * Example:
 * 1. Having date: 23-Dec-2025 with local TZ EET (+2:00 from UTC).
 * 2. Without the fix: DevExtreme will convert this to: 22-Dec-2025 22:00 (removing the +2:00 from UTC).
 * 3. When we use it as date, we will get 22-Dec-2025, which is incorrect.
 *
 * Applying the fix between step 1 and 2, we will set the date to: 23-Dec-2025 2:00 (EET).
 * On step 2, DX will convert it to: 23-Dec-2025 00:00 (UTC).
 * On step 3, we will use the correct date 23-Dec-2025.
 */
export const fixDateTZ = (date: Date) => {
  const isStartOfDay = date.getUTCHours() === 0 && date.getUTCMinutes() === 0;
  if (isStartOfDay) {
    return date;
  }

  date.setTime(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
  return date;
};
